import { AionCta } from "@/components/site/AionCta";
import { OG_ABOUT } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ScrollText } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Biography — Wiki, Career & Life Story";
const DESC =
  "Raj Shamani biography: born in Indore, schooling at Daly College, Shamani Industries, the Figuring Out podcast, Build Don't Talk and House of X — his full career timeline.";
const URL = "https://rajshamani.life/raj-shamani-biography";
const OG = OG_ABOUT;

const timeline = [
  {
    year: "1996",
    title: "Born in Indore",
    body: "Raj Shamani is born into a Marwari business family in Indore, Madhya Pradesh, where his father runs the family manufacturing business.",
  },
  {
    year: "2000s",
    title: "Daly College, Indore",
    body: "Schooling at Daly College — the years he credits for public speaking confidence and an early comfort with being on stage.",
  },
  {
    year: "2012",
    title: "First business at 16",
    body: "Joins and then runs distribution work inside Shamani Industries, learning margins, cash flow and retail distribution before college ended.",
  },
  {
    year: "2017–2019",
    title: "Speaking and content",
    body: "Begins speaking at conferences and publishing consistently online — years of unpaid output that later became the distribution engine for everything else.",
  },
  {
    year: "2020",
    title: "Figuring Out launches",
    body: "Starts the Figuring Out podcast, built around long-form conversations with founders, investors, athletes and public figures.",
  },
  {
    year: "2022",
    title: "Build, Don't Talk",
    body: "Publishes his first book, arguing that credibility is earned by shipping work rather than announcing plans.",
  },
  {
    year: "2022–present",
    title: "House of X and investing",
    body: "Co-builds the House of X venture studio and backs early-stage Indian consumer and D2C startups as an angel investor.",
  },
];

const faq = [
  {
    q: "Who is Raj Shamani?",
    a: "Raj Shamani is an Indian entrepreneur, investor, author and content creator. He hosts the Figuring Out podcast, wrote Build, Don't Talk, works in the family business Shamani Industries and co-builds the D2C venture studio House of X.",
  },
  {
    q: "Where is Raj Shamani from?",
    a: "He is from Indore, Madhya Pradesh, and belongs to a Marwari business family. He now works primarily out of Mumbai.",
  },
  {
    q: "What is Raj Shamani famous for?",
    a: "He is best known for the Figuring Out podcast, one of India's most-watched long-form interview shows, and for his book Build, Don't Talk.",
  },
  {
    q: "What did Raj Shamani study?",
    a: "He studied at Daly College in Indore and completed his graduation in India. He has spoken publicly about choosing operating experience over an MBA.",
  },
  {
    q: "Is there a Raj Shamani Wikipedia page?",
    a: "Coverage of Raj Shamani is scattered across interviews and press profiles. This page and the About page collect the verifiable details — birthplace, education, businesses and career milestones — in one place.",
  },
];

export const Route = createFileRoute("/raj-shamani-biography")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani biography", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          mainEntity: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: BiographyPage,
});

function BiographyPage() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Biography" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <ScrollText size={14} /> Biography & wiki
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Raj Shamani <span className="text-gradient-gold">biography</span> — the full life story
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            From an Indore business family to one of India&apos;s most-watched long-form
            interviewers: his early years, education, businesses, book and investments, with links
            to the detail pages for{" "}
            <Link to="/raj-shamani-age" className="text-gold underline underline-offset-4">
              age
            </Link>
            ,{" "}
            <Link to="/raj-shamani-net-worth" className="text-gold underline underline-offset-4">
              net worth
            </Link>{" "}
            and{" "}
            <Link to="/raj-shamani-family" className="text-gold underline underline-offset-4">
              family
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Career timeline</h2>
        <ol className="space-y-4">
          {timeline.map((t) => (
            <li key={t.year} className="rounded-xl border border-border bg-card p-6">
              <div className="text-sm text-gold font-mono">{t.year}</div>
              <h3 className="font-bold mt-1">{t.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mt-2">{t.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">Early life and education</h2>
        <div className="space-y-5 text-muted-foreground leading-relaxed max-w-3xl">
          <p>
            Raj Shamani grew up in Indore in a Marwari family where business was the dinner-table
            subject rather than a career choice made later. That upbringing shows up in how he talks
            about money on the podcast: operationally, in terms of margins, distribution and cash,
            rather than as abstract advice. His{" "}
            <Link to="/raj-shamani-education" className="text-gold underline underline-offset-4">
              education
            </Link>{" "}
            at Daly College gave him the stage confidence that later made speaking and interviewing
            feel natural.
          </p>
          <p>
            He began working inside{" "}
            <Link to="/shamani-industries" className="text-gold underline underline-offset-4">
              Shamani Industries
            </Link>{" "}
            as a teenager, handling distribution and retail relationships. That period is the reason
            he argues so persistently that distribution, not the product, is the hard part of any
            consumer business.
          </p>
          <p>
            The public phase of his career started with speaking engagements and years of daily
            posting before{" "}
            <Link to="/raj-shamani-podcast" className="text-gold underline underline-offset-4">
              Figuring Out
            </Link>{" "}
            launched. The podcast, the book{" "}
            <Link to="/build-dont-talk" className="text-gold underline underline-offset-4">
              Build, Don&apos;t Talk
            </Link>{" "}
            and his{" "}
            <Link to="/raj-shamani-business" className="text-gold underline underline-offset-4">
              businesses
            </Link>{" "}
            all compound off the same base: work published consistently, for a long time, before it
            paid.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/raj-shamani-biography" />

      <RelatedPages pages={["aion", "about", "podcast", "book", "ventures", "faq"]} />
    </div>
  );
}
